/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefObject } from 'preact';
import { MutableRef, useLayoutEffect, useState } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import { ItemInfo, LegendSectionData, LegendItemData } from '#utils/UNSAFE_visTypes/legend';
import { DATATIP_OFFSET } from '#utils/UNSAFE_visUtils/datatipUtils';
import { useDatatip } from '#hooks/PRIVATE_useDatatip';
import { DatatipConfig } from '#utils/UNSAFE_visTypes/common';
import { LegendDatatipContext } from './legend.type';

type props<K, D, S> = {
  items?: D[];

  sections?: S[];

  rootDimsRef: MutableRef<DOMRect | undefined>;

  focusedItemRef: RefObject<HTMLDivElement>;

  focusedItemInfo: ItemInfo;

  hoveredItemInfo?: ItemInfo;

  touchResponse: 'touchStart' | 'touchHold';

  isDisabled: boolean;

  datatip?: DatatipConfig<LegendDatatipContext<K, D>>;
};

/**
 * Calculate the datatip offset for datatip when keyboard focus is active.
 * @param focusedItemRef The ref for currentItem node.
 * @param rootRef The ref for root of the legend.
 * @param currentItem The ItemInfo of the current keyboard focused item.
 * @returns
 */
const calculateOffset = (
  focusedItemRef: RefObject<HTMLDivElement>,
  rootDimsRef: MutableRef<DOMRect | undefined>,
  currentItem: ItemInfo,
  isRtl: boolean
) => {
  let crossAxis = 0;
  let mainAxisOffset = DATATIP_OFFSET;
  if (focusedItemRef.current && currentItem.isCurrent && rootDimsRef.current) {
    const rootDim = rootDimsRef.current;
    const itemDim = focusedItemRef.current.getBoundingClientRect();
    crossAxis = itemDim.x + itemDim.width / 2 - (rootDim.x + rootDim.width / 2);
    crossAxis = isRtl ? -1 * crossAxis : crossAxis;
    mainAxisOffset += rootDim.y - itemDim.y;
    mainAxisOffset = Math.min(
      DATATIP_OFFSET,
      Math.max(mainAxisOffset, itemDim.height - rootDim.height + DATATIP_OFFSET)
    );
  }
  return { crossAxis, mainAxis: mainAxisOffset };
};

/**
 * Custom hook to manage datatip (tooltip) display for Legend and Sectional Legend.
 *
 * This hook calculates the appropriate offset and content for a datatip based on keyboard focus,
 * hover state, and legend structure. It supports both sectioned and flat legend data, RTL direction,
 * and touch interactions.
 *
 * @template K - Key type for legend items (string or number)
 * @template D - Legend item data type
 * @template S - Legend section data type
 * @returns {ReturnType<typeof useDatatip>} Datatip configuration for rendering
 */
export function useLegendDatatip<
  K extends string | number,
  D extends LegendItemData<K>,
  S extends LegendSectionData<K, D>
>({
  rootDimsRef,
  focusedItemRef,
  focusedItemInfo,
  touchResponse,
  isDisabled,
  datatip,
  items,
  hoveredItemInfo,
  sections
}: props<K, D, S>) {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const [offset, setOffset] = useState<{ mainAxis?: number; crossAxis?: number }>(
    calculateOffset(focusedItemRef, rootDimsRef, focusedItemInfo, isRtl)
  );

  useLayoutEffect(() => {
    setOffset(calculateOffset(focusedItemRef, rootDimsRef, focusedItemInfo, isRtl));
  }, [focusedItemInfo, focusedItemRef, isRtl, rootDimsRef]);

  let content: ComponentChildren = undefined;
  let borderColor: string | undefined = undefined;
  let defaultContainer: 'enabled' | 'disabled' | undefined = undefined;

  const activeItemInfo = focusedItemInfo?.isCurrent
    ? focusedItemInfo
    : hoveredItemInfo?.isCurrent
    ? hoveredItemInfo
    : undefined;
  if (activeItemInfo) {
    const { itemIdx, sectionIdx } = activeItemInfo;
    const activeItem =
      sections && sections.length > 0
        ? sections[sectionIdx].items[itemIdx]
        : items && items[itemIdx];

    if (activeItem) {
      const detail: LegendDatatipContext<K, D> = {
        data: activeItem,
        itemIndex: itemIdx,
        sectionIndex: sectionIdx,
        id: activeItem.id
      };
      content = activeItem.datatip;
      const config = datatip?.(detail);
      if (config) {
        content = config.content;
        borderColor = config.borderColor;
        defaultContainer = config.defaultContainer;
      }
    }
  }

  const anchor = focusedItemInfo?.isCurrent ? 'element' : 'pointer';

  return useDatatip({
    content,
    placement: 'top',
    anchor,
    offset,
    touchResponse,
    isDisabled,
    borderColor,
    defaultContainer
  });
}
